import React from "react";
import AuditGroupHeader from "./auditGroupHeader/AuditGroupHeader";
import { useTranslation } from "../../localization/useTranslation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../useUrlProvider";
import { useLegalEntityAuditGroupDialog } from "./LegalEntityAuditGroupOverview.hooks";
import { DotLegalCompletedPercentage } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import EntityDetailTopBar from "@common/components/entityDetailTopBar/EntityDetailTopBar";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import TopBarNumberTile from "@common/components/entityDetailTopBar/TopBarNumberTile";
import TopBarTile, { useTopBarTileSize } from "@common/components/entityDetailTopBar/TopBarTile";
import TopBarRiskLevelAndScoreTile from "@common/components/entityDetailTopBar/TopBarRiskLevelAndScoreTile";
import { LegalEntityAuditStatus } from "../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types";
import DotLegalLink from "../../common/components/dotLegalLink/DotLegalLink";
import QuestionnaireTemplateLink from "../../legalEntity/legalEntityAuditTab/QuestionnaireTemplateLink.tsx";
import { usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import RelatedTasksTab from "../../taskManagement/relatedTasksTab/RelatedTasksTab.tsx";
import { EntityType } from "../../common/Common.types.ts";
import LegalEntityAuditGroupAuditsTab from "./legalEntityAuditGroupAuditsTab/LegalEntityAuditGroupAuditsTab.tsx";
import { AuditGroupTabs } from "./LegalEntityAuditGroupOverview.types.ts";
import { useGetRelatedTaskData } from "../../taskManagement/relatedTasksTab/RelatedTasksTab.hooks.ts";
import { getPlanTab } from "../../plan/planTab/PlanTab.tsx";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import PlanTabs, { PlanTabContent } from "../../plan/planTab/PlanTabs.tsx";

function LegalEntityAuditGroupOverview() {
    const { translateString, translateDate, translateLegalEntityAuditStatus } = useTranslation();
    const { customerName, permissions } = useUserContext();
    const { getAuditGroupsUrl, getGroupEntityUrl, getAuditGroupTaskApiUrl } = useUrlProvider();
    const { vendorManagementFeatures, addonsFeatures, platformFeatures } = usePlanContext();
    const { auditGroupId, isLoading, data, onAuditGroupUpdated, refetch, selectedTab, setSelectedTab } = useLegalEntityAuditGroupDialog();
    const { getTaskTabHeader } = useGetRelatedTaskData(auditGroupId, getAuditGroupTaskApiUrl(auditGroupId));
    const tileSize = useTopBarTileSize();

    const getTabContent = () => {
        const content: Array<PlanTabContent> = [];

        content.push({
            label: translateString("audits"),
            content: <LegalEntityAuditGroupAuditsTab auditGroupId={auditGroupId} isLoading={isLoading} auditsData={data} refetchData={refetch} />,
            index: AuditGroupTabs.Audits,
        });

        if (permissions.taskPermissions.read && !addonsFeatures.research) {
            content.push(
                getPlanTab(
                    getTaskTabHeader(),
                    <RelatedTasksTab
                        forEntityId={auditGroupId}
                        forEntityType={EntityType.LegalEntityAuditGroup}
                        forEntityName={data?.name ?? ""}
                        customApiUrl={getAuditGroupTaskApiUrl(auditGroupId)}
                        additionalHeaderTypeTableHeader={translateString("createdFrom")}
                    />,
                    AuditGroupTabs.Tasks,
                    platformFeatures.canAccessRelationsBetweenEntities,
                    translateString("upgradePrivacyPlanToday")
                )
            );
        }

        return content;
    };

    return (
        <React.Fragment>
            <AuditGroupHeader
                breadCrumbs={[
                    { name: translateString("externalAudits"), link: getAuditGroupsUrl() },
                    {
                        name: data?.name,
                        tooltip: isNullOrWhitespace(data?.description) ? undefined : <MentionInputRender value={data?.description ?? ""} linkColor={"white"} />,
                    },
                ]}
                customerName={customerName}
                onAuditGroupUpdated={onAuditGroupUpdated}
                auditGroupModel={
                    data
                        ? {
                              id: data.id,
                              auditTemplateId: data!.auditTemplateId,
                              auditTemplateName: data.auditTemplate,
                              auditTemplateVersion: data.auditTemplateVersion,
                              deadline: data.deadline,
                              name: data.name,
                              responsibleId: data.responsibleId,
                              description: data.description,
                              senderId: data.senderId,
                              sender: data.sender,
                              sent: data.sent,
                          }
                        : undefined
                }
                showAddLegalEntityBtn
                showDeleteMenuItem
                canDeleteAuditgroup={data !== undefined && data.audits.filter((x) => x.status === LegalEntityAuditStatus.Submitted).length === 0}
            />

            <EntityDetailTopBar>
                <TopBarRow>
                    <TopBarTextTile
                        label={translateString("status")}
                        isLoading={isLoading}
                        value={
                            data
                                ? data.progress === 100
                                    ? translateString("completed")
                                    : data?.sent
                                      ? translateLegalEntityAuditStatus(LegalEntityAuditStatus.InProgress.toString())
                                      : ""
                                : ""
                        }
                    />
                    <TopBarTextTile label={translateString("sent")} isLoading={isLoading} value={data?.sent ? translateDate(data.sent) : ""} />
                    <TopBarTextTile
                        label={translateString("auditTemplate")}
                        isLoading={isLoading}
                        value={
                            data ? (
                                vendorManagementFeatures.auditGroups ? (
                                    <QuestionnaireTemplateLink id={data.auditTemplateId} name={data.auditTemplate} type={"audit"} />
                                ) : (
                                    data.auditTemplate
                                )
                            ) : (
                                ""
                            )
                        }
                    />
                    <TopBarTextTile label={translateString("deadline")} isLoading={isLoading} value={data ? translateDate(data.deadline) : ""} />
                    <TopBarTextTile label={translateString("responsible")} isLoading={isLoading} value={data ? data.responsible : ""} />
                    <TopBarTextTile
                        label={translateString("sentFrom")}
                        isLoading={isLoading}
                        value={
                            data ? (
                                data.sender ? (
                                    data.senderId ? (
                                        <DotLegalLink bold linkColor={"primary"} to={getGroupEntityUrl(data.senderId)}>
                                            {data.sender}
                                        </DotLegalLink>
                                    ) : (
                                        data.sender
                                    )
                                ) : (
                                    customerName
                                )
                            ) : (
                                ""
                            )
                        }
                    />
                </TopBarRow>
                <TopBarRow>
                    <TopBarNumberTile label={translateString("legalEntities")} isLoading={isLoading} value={data?.numberOfLegalEntities} />
                    <TopBarNumberTile label={translateString("opened")} isLoading={isLoading} value={data?.openedAuditsCount} />
                    <TopBarNumberTile label={translateString("submitted")} isLoading={isLoading} value={data?.submittedAuditsCount} />
                    <TopBarNumberTile label={translateString("excluded")} isLoading={isLoading} value={data?.excludedAuditsCount} />
                    {data && data.scoreEnabled && (
                        <TopBarRiskLevelAndScoreTile
                            isLoading={isLoading}
                            scoreEnabled={data.scoreEnabled}
                            riskLevel={data.overallRiskLevel}
                            riskLevelName={data.overallRiskLevelName}
                            score={data.overallRiskScore}
                        />
                    )}
                    <TopBarTile label={translateString("progress")} isLoading={isLoading}>
                        {data ? (
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <Box width={238}>
                                    <DotLegalCompletedPercentage completed={data.progress} />
                                </Box>
                            </Box>
                        ) : undefined}
                    </TopBarTile>
                    {data && !data.scoreEnabled && <Grid size={tileSize} />}
                </TopBarRow>
            </EntityDetailTopBar>

            <PlanTabs
                fullWidth={false}
                tabStyle="secondary"
                selectedTab={selectedTab!}
                setSelectedTab={(val) => setSelectedTab(val)}
                content={getTabContent()}
                noPadding
            />
        </React.Fragment>
    );
}

export default LegalEntityAuditGroupOverview;
